import { computed, inject, untracked } from '@angular/core';
import { LocaleService } from '@next/core-lib/i18n';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { SeriesOptionsType } from 'highcharts';

import { DateService } from 'src/app/shared/services/date.service';
import { toLocaleSignal } from 'src/app/shared/utils';
import { CropFieldAdvice } from '../disease-overview-table.component/models/models';

import { CHART_CONFIG, toUTCDateMs } from './base-chart.config';
import { DiseaseChartDay } from './models';

interface DiseaseInsightChartState {
  readonly rawChartDays: DiseaseChartDay[];
  readonly selectedRange: Date[];
  readonly renderedRange: Date[];
  readonly advice: CropFieldAdvice | undefined;
}

const initialState: DiseaseInsightChartState = {
  rawChartDays: [],
  selectedRange: [],
  renderedRange: [],
  advice: undefined,
};

export const DiseaseInsightChartStore = signalStore(
  withState(initialState),

  withComputed(
    (
      store,
      dateService = inject(DateService),
      localeService = inject(LocaleService),
      chartConfig = inject(CHART_CONFIG),
    ) => {
      const filterDaysByRange = (days: DiseaseChartDay[], range: Date[], offsetDays = 0): DiseaseChartDay[] => {
        if (!days?.length || !range?.length) return days ?? [];

        const from = new Date(range[0]);
        const to = new Date(range[range.length - 1]);

        if (offsetDays) {
          from.setDate(from.getDate() - offsetDays);
          to.setDate(to.getDate() + offsetDays);
        }

        return days.filter((day) => {
          const d = new Date(day.date);
          return dateService.isDateLaterThanOrEquals(d, from) && dateService.isDateEarlierThanOrEquals(d, to);
        });
      };

      const activeRange = computed(() => {
        const rendered = store.renderedRange();
        return rendered.length ? rendered : store.selectedRange();
      });

      // Extended range (±1 day) for chart graph data continuity
      const filteredChartDays = computed(() => filterDaysByRange(store.rawChartDays(), activeRange(), 1));

      const locale = toLocaleSignal(localeService);

      const selectedChartDays = computed(() => filterDaysByRange(store.rawChartDays(), activeRange()));

      return {
        locale,
        chartDays: selectedChartDays,
        hasAdvices: computed(() => (store.advice()?.advices?.length ?? 0) > 0),
        hasAdvicesForSelectedRange: computed(
          () => (store.advice()?.advices?.length ?? 0) > 0 && selectedChartDays()?.length > 0,
        ),

        isPlantDateInFuture: computed(() => {
          const adv = store.advice();
          if (!adv?.plantDate) return false;
          return dateService.isInTheFuture(new Date(adv.plantDate));
        }),

        isChartVisible: computed(() => !!filteredChartDays()?.length),

        chartSeriesOptions: computed((): Highcharts.Options => {
          const days = selectedChartDays();
          if (!days?.length) return {};

          const firstDate = new Date(days[0].date);
          const lastDate = new Date(days.at(-1).date);
          const plotBands = buildPlotBands([firstDate, lastDate]);
          const xMin = toUTCDateMs(firstDate);
          const xMax = toUTCDateMs(lastDate) + 24 * 60 * 60 * 1000;

          return chartConfig.getChartSeriesOptions(plotBands, xMin, xMax, locale(), localeService);
        }),

        emergenceDateMs: computed((): number | undefined => {
          const emergenceDateValue = store.advice()?.emergenceDate;
          if (!emergenceDateValue) return undefined;

          const range = activeRange();
          if (
            range?.length >= 2 &&
            dateService.isDateLaterThanOrEquals(new Date(emergenceDateValue), new Date(range[0])) &&
            dateService.isDateEarlierThanOrEquals(new Date(emergenceDateValue), new Date(range[1]))
          ) {
            return new Date(emergenceDateValue).getTime();
          }
          return undefined;
        }),

        chartDataSeries: computed((): SeriesOptionsType[] => {
          // Read locale() to retrigger this computed when locale changes
          locale();

          const days = filteredChartDays();
          if (!days?.length) return [];

          const emergenceDateValue = store.advice()?.emergenceDate;
          let emergenceDateMs: number | undefined;
          if (emergenceDateValue) {
            const range = activeRange();
            if (
              range?.length >= 2 &&
              dateService.isDateLaterThanOrEquals(new Date(emergenceDateValue), new Date(range[0])) &&
              dateService.isDateEarlierThanOrEquals(new Date(emergenceDateValue), new Date(range[1]))
            ) {
              emergenceDateMs = new Date(emergenceDateValue).getTime();
            }
          }

          return chartConfig.getSeriesOptions(localeService, days, emergenceDateMs);
        }),
      };
    },
  ),

  withMethods((store) => ({
    setChartDays(chartDays: DiseaseChartDay[]): void {
      // Read store signals with untracked() so that callers inside an effect()
      // don't accidentally track selectedRange / renderedRange as dependencies,
      // which would cause the chartDays effect to re-run every time those change.
      const [selectedRange, renderedRange, currentRawDays] = untracked(() => [
        store.selectedRange(),
        store.renderedRange(),
        store.rawChartDays(),
      ]);

      // Cache previous data: if new data is empty but we have existing data, keep it
      // This prevents UI flicker during date range changes while API is loading
      if (chartDays.length === 0 && currentRawDays.length > 0) {
        return;
      }

      const shouldApplySelectedRange = hasDataForRange(chartDays, selectedRange);

      patchState(store, {
        rawChartDays: chartDays,
        renderedRange: shouldApplySelectedRange ? selectedRange : renderedRange,
      });
    },
    setSelectedRange(selectedRange: Date[]): void {
      patchState(store, { selectedRange });
    },
    setAdvice(advice: CropFieldAdvice | undefined): void {
      patchState(store, { advice });
    },
  })),
);

function hasDataForRange(days: DiseaseChartDay[], range: Date[]): boolean {
  if (!days?.length || !range?.length) {
    return false;
  }

  const from = new Date(range[0]);
  const to = new Date(range[range.length - 1]);

  return days.some((day) => {
    const d = new Date(day.date);
    return d >= from && d <= to;
  });
}

function buildPlotBands(dates: Date[]): Highcharts.XAxisPlotBandsOptions[] {
  return dates.map((day) => {
    const date = new Date(day);
    const utc = toUTCDateMs(date);
    return { from: utc, to: utc, color: 'white' };
  });
}
